import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import {
  useCreateStockIn,
  useListInventoryItems,
  getListStockInQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";

// Phase 2.1 polish D — Daily Pickups quick form for milk/dairy/etc bought from
// local shops. Strips out PO/QC/financial sections — operator just records:
// date, item (Daily-frequency only), qty, vendor name, payment mode, amount, photo URL.

const DailyPickupSchema = z
  .object({
    transactionDate: z.date({ required_error: "Pick a date" }),
    itemId: z.coerce.number().int().positive("Pick an item"),
    quantity: z.coerce.number().positive("Quantity must be > 0"),
    vendorNameFreeText: z.string().min(1, "Shop name is required"),
    paymentMode: z.enum(["Cash", "On_Account"]),
    cashPaid: z.coerce.number().nonnegative().nullable().optional(),
    photoUrl: z.string().url("Must be a valid URL").or(z.literal("")).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.paymentMode === "Cash" && (data.cashPaid == null || data.cashPaid <= 0)) {
      ctx.addIssue({ code: "custom", path: ["cashPaid"], message: "Enter the amount paid" });
    }
  });

type FormValues = z.infer<typeof DailyPickupSchema>;

interface Props {
  onSaved?: () => void;
  onDiscard?: () => void;
}

export function DailyPickupForm({ onSaved, onDiscard }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: items = [] } = useListInventoryItems();
  const createMutation = useCreateStockIn();
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const dailyItems = useMemo(
    () => items.filter((i) => (i as { stockInFrequency?: string }).stockInFrequency === "Daily" && i.status === "Active"),
    [items],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(DailyPickupSchema),
    defaultValues: {
      transactionDate: new Date(),
      itemId: undefined as unknown as number,
      quantity: undefined as unknown as number,
      vendorNameFreeText: "",
      paymentMode: "Cash",
      cashPaid: null,
      photoUrl: "",
    },
  });

  const paymentMode = form.watch("paymentMode");

  async function onSubmit(values: FormValues) {
    // R1 (migration 0046): per-receipt paymentStatus / paymentDueDate retired —
    // payment is recorded once on the parent PO. Cash vs On_Account now only
    // controls whether finalPaidAmount is set on this row.
    const isCash = values.paymentMode === "Cash";
    const unitPrice = isCash && values.quantity > 0 && values.cashPaid != null
      ? values.cashPaid / values.quantity
      : 0;

    try {
      await createMutation.mutateAsync({
        data: {
          transactionDate: values.transactionDate.toISOString(),
          transactionType: "Daily_Purchase",
          vendorNameFreeText: values.vendorNameFreeText,
          paymentMode: values.paymentMode,
          // R1 (migration 0046): per-receipt paymentStatus / paymentDueDate retired.
          finalPaidAmount: isCash ? values.cashPaid! : null,
          invoiceScreenshotUrl: values.photoUrl || null,
          lineItems: [{
            itemId: values.itemId,
            quantityReceived: values.quantity,
            unitPriceActual: unitPrice,
          }],
        },
      });
      toast({ title: "Daily pickup recorded." });
      queryClient.invalidateQueries({ queryKey: getListStockInQueryKey() });
      onSaved?.();
    } catch {
      toast({ title: "Could not save. Please try again.", variant: "destructive" });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 px-4 pt-2 pb-6 max-w-md">
        <div>
          <h2 className="text-lg font-semibold">Add Daily Pickup</h2>
          <p className="text-xs text-muted-foreground">Quick log for milk / dairy / daily perishables from local shops.</p>
        </div>

        <FormField
          name="transactionDate"
          control={form.control}
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Date <span className="text-destructive">*</span></FormLabel>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value ? format(field.value, "PPP") : "Pick a date"}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={(d) => { if (d) { field.onChange(d); setDatePickerOpen(false); } }}
                    disabled={(d) => d > new Date()}
                    className="p-4 [--cell-size:2.5rem]"
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="itemId"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Item <span className="text-destructive">*</span></FormLabel>
              <Select value={field.value ? String(field.value) : ""} onValueChange={(v) => field.onChange(Number(v))}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={dailyItems.length === 0 ? "No items marked as Daily" : "Pick a daily item"} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {dailyItems.map((it) => (
                    <SelectItem key={it.id} value={String(it.id)}>
                      {it.name} <span className="text-muted-foreground">({it.unitOfMeasurement})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dailyItems.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Mark items as Daily in the Items tab (Stock-In Frequency = Daily) to make them appear here.
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="quantity"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Quantity <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 10"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="vendorNameFreeText"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Local Shop / Vendor <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input placeholder="e.g. Mother Dairy Najafgarh" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="paymentMode"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Payment <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <RadioGroup value={field.value} onValueChange={field.onChange} className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Cash" id="pay-cash" />
                    <label htmlFor="pay-cash" className="text-sm">Cash now</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="On_Account" id="pay-account" />
                    <label htmlFor="pay-account" className="text-sm">Month-end (on account)</label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {paymentMode === "Cash" && (
          <FormField
            name="cashPaid"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amount Paid (₹) <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="e.g. 600"
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {paymentMode === "On_Account" && (
          <p className="text-xs text-muted-foreground -mt-2">
            Logged as Pending. Settle at month-end with the shop's running tab.
          </p>
        )}

        <FormField
          name="photoUrl"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Photo of Bill (URL)</FormLabel>
              <FormControl>
                <Input
                  type="url"
                  placeholder="Optional — paste link from WhatsApp / Drive"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              </FormControl>
              <p className="text-xs text-muted-foreground">In-app upload coming next phase.</p>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onDiscard}>Discard</Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? "Saving…" : "Save Daily Pickup"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
